import { defineStore } from "pinia";
import authService from "../services/auth.service.ts";

export interface PersistentIPDevice {
  id: string;
  name: string;
  ip: string;
  // Per-device set of ACL IDs this IP may reach.
  aclIds: string[];
  createdAt: string;
  updatedAt: string;
}

export interface PersistentIPDeviceInput {
  name: string;
  ip: string;
  aclIds: string[];
  ticketNumber?: string;
  totpCode?: string;
}

export interface BatchResult {
  aclID: string;
  success: boolean;
  error?: string;
}

let fetchPromise: Promise<void> | null = null;

export const usePersistentIPDeviceStore = defineStore("persistentIPDevices", {
  state: () => ({
    devices: [] as PersistentIPDevice[],
    loaded: false,
    // Last mutation error, held here (not in the panel) so it survives the
    // access menu closing and remounting the panel. Cleared when the next
    // mutation starts.
    lastError: null as string | null,
  }),

  actions: {
    async fetchDevices() {
      if (this.loaded) return;
      if (fetchPromise) return fetchPromise;

      fetchPromise = (async () => {
        try {
          this.devices = (await authService.listPersistentIPDevices()) ?? [];
          this.loaded = true;
        } catch {
          this.devices = [];
          this.loaded = true;
        } finally {
          fetchPromise = null;
        }
      })();

      return fetchPromise;
    },

    upsertDevice(device: PersistentIPDevice) {
      const i = this.devices.findIndex((d) => d.id === device.id);
      if (i === -1) {
        this.devices.push(device);
      } else {
        this.devices[i] = device;
      }
    },

    // Mutations save and reconcile grants in one server call; the returned
    // results carry per-knoc grant outcomes (empty when the session holds no
    // seat, the rows still saved).
    async createDevice(data: PersistentIPDeviceInput): Promise<BatchResult[]> {
      const resp = await authService.createPersistentIPDevice(data);
      this.upsertDevice(resp.device);
      return resp.results ?? [];
    },

    async updateDevice(deviceId: string, data: PersistentIPDeviceInput): Promise<BatchResult[]> {
      const resp = await authService.updatePersistentIPDevice(deviceId, data);
      this.upsertDevice(resp.device);
      return resp.results ?? [];
    },

    async deleteDevice(deviceId: string): Promise<void> {
      await authService.deletePersistentIPDevice(deviceId);
      this.devices = this.devices.filter((d) => d.id !== deviceId);
    },

    async setDeviceEnabled(
      deviceId: string,
      data: { enabled: boolean; ticketNumber?: string; totpCode?: string },
    ): Promise<BatchResult[]> {
      const resp = await authService.setPersistentIPDeviceEnabled(deviceId, data);
      this.upsertDevice(resp.device);
      return resp.results ?? [];
    },

    async setAllEnabled(data: { enabled: boolean; ticketNumber?: string; totpCode?: string }): Promise<BatchResult[]> {
      const resp = await authService.setAllPersistentIPDevicesEnabled(data);
      this.devices = resp.devices ?? [];
      return resp.results ?? [];
    },

    async batchAddPersistentIPs(
      aclIDs: string[],
      persistentIPs: string[],
      ticketNumber?: string,
      totpCode?: string,
    ): Promise<BatchResult[]> {
      return authService.batchAddPersistentIPs({ aclIDs, persistentIPs, ticketNumber, totpCode });
    },
  },
});
